// ============================================
// FILE: src/components/HomeScreen.jsx
// ============================================

import React, { useState, useEffect } from 'react';
import { Search, Bell, MapPin, Menu, Heart, Gift, Package, Plus, ShoppingCart } from 'lucide-react';

import LocationModal from './modals/LocationModal';
import ProductDetailsModal from './modals/ProductDetailsModal';
import { banners, locations, subscriptionPlans,categoryIcons } from '../data/products';
import { getProductImage, getProductEmoji } from '../utils/imageHelpers';

 
const HomeScreen = ({
  searchQuery = '',
  setSearchQuery,
  setShowNotifications,
  setShowLocationModal,
  showLocationModal,
  selectedLocation,
  setSelectedLocation,
  currentBanner,
  setCurrentBanner,
  addToCart,
  setShowSubscription,
  products,
  filteredProducts,
  cart = [],
  getCartCount,
  openCheckout,
  userProfile = {},
  notifications = [],
  favorites = [],
  setShowFavorites,
  setFavorites,
  toggleFavorite,
  setActiveTab,
  selectedCategory = 'All',
  setSelectedCategory,
  showToast,
  appSettings
}) => {
  const favArray = Array.isArray(favorites) ? favorites : [];
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 40;

  // Auto-advance banner carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000); // Change banner every 5 seconds

    return () => clearInterval(timer);
  }, [setCurrentBanner]);

  // Reset to page 1 when category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  // Extract unique categories from products
  const categories = ['All', ...new Set(products.map(p => {
    // Handle both API response (category object) and static data (category string)
    if (typeof p.category === 'object' && p.category?.name) {
      return p.category.name;
    }
    return p.category || 'Uncategorized';
  }).filter(Boolean))];
  
 
  return (
    <div className="pb-20">
      {/* Interactive Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white pb-6 rounded-b-3xl shadow-lg">
        {/* Top Bar */}
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <button className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all">
            <Menu size={24} />
          </button>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowNotifications(true)}
              className="relative p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
            >
              <Bell size={24} />
              {notifications.filter(n => n.unread).length > 0 && (
                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
                  <span className="text-[10px] font-bold text-white">
                    {notifications.filter(n => n.unread).length}
                  </span>
                </span>
              )}
            </button>
            
            <button 
              onClick={() => setShowFavorites(true)}
              className="relative p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
            >
              <Heart size={24} />
                {favArray.length > 0 && (
                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-pink-500 rounded-full border-2 border-white flex items-center justify-center">
                  <span className="text-[10px] font-bold text-white">
                      {favArray.length}
                  </span>
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Location & Greeting */}
     
        
        <div className="px-4 mb-3">
          <p className="text-blue-100 text-sm mb-1">
            Good Morning! 👋 {userProfile?.name && <span className="font-semibold">{userProfile.name}</span>}
          </p>
          <h1 className="text-2xl font-bold mb-3">{appSettings?.appName || 'Fresh Milk Delivery'}</h1>
          
          <button 
            onClick={() => setShowLocationModal(true)}
            className="flex items-center gap-2 bg-white bg-opacity-20 px-3 py-2 rounded-lg hover:bg-opacity-30 transition-all"
          >
            <MapPin size={16} />
            <span className="text-sm font-medium">{selectedLocation}</span>
            <span className="text-xs">▼</span>
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-4">
          <div className="bg-white rounded-full flex items-center px-4 py-3 shadow-md">
            <Search className="text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search for fruits, milk, paneer, curd..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 ml-3 outline-none text-gray-800"
            />
          </div>
        </div>

        {/* Quick Action Buttons */}
        <div className="px-4 mt-4 flex gap-3 overflow-x-auto">
          <button 
            onClick={() => {
              // Scroll to banner section which shows offers
              const bannerElement = document.querySelector('.banner-carousel');
              if (bannerElement) {
                bannerElement.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="flex items-center gap-2 bg-white bg-opacity-20 px-4 py-2 rounded-full whitespace-nowrap hover:bg-opacity-30 transition-all"
          >
            <Gift size={16} />
            <span className="text-sm font-medium">Offers</span>
          </button>
          <button 
            onClick={() => setActiveTab && setActiveTab('orders')}
            className="flex items-center gap-2 bg-white bg-opacity-20 px-4 py-2 rounded-full whitespace-nowrap hover:bg-opacity-30 transition-all"
          >
            <Package size={16} />
            <span className="text-sm font-medium">Track Order</span>
          </button>
          <button 
            onClick={() => setShowSubscription((filteredProducts && filteredProducts.length>0) ? filteredProducts[0] : (products && products.length>0) ? products[0] : null)}
            className="flex items-center gap-2 bg-white bg-opacity-20 px-4 py-2 rounded-full whitespace-nowrap hover:bg-opacity-30 transition-all"
          >
            <span className="text-sm font-medium">🔔 Subscribe</span>
          </button>
        </div>
      </div>

      {/* Banner Carousel */}
      <div className="px-4 mt-4 mb-6 banner-carousel">
        <div className="relative overflow-hidden rounded-2xl shadow-lg">
          <div 
            className="flex banner-slide"
            style={{ 
              transform: `translateX(-${currentBanner * 100}%)`,
              transition: 'transform 0.5s ease-in-out'
            }}
          >
            {banners.map((banner) => (
              <div
                key={banner.id}
                className={`min-w-full bg-gradient-to-r ${banner.background} p-6 flex items-center justify-between`}
              >
                <div className="flex-1">
                  <span className="bg-white bg-opacity-30 text-white text-xs px-3 py-1 rounded-full font-medium mb-2 inline-block">
                    {banner.offer}
                  </span>
                  <h2 className="text-2xl font-bold text-white mb-1">
                    {banner.title}
                  </h2>
                  <p className="text-white text-opacity-90 text-sm mb-3">
                    {banner.subtitle}
                  </p>
                  <button 
                    onClick={() => {
                      const productsElement = document.querySelector('.products-section');
                      if (productsElement) {
                        productsElement.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="bg-white text-blue-600 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-opacity-90 transition-all"
                  >
                    Shop Now
                  </button>
                </div>
                <div className="text-6xl ml-4">
                  {banner.icon}
                </div>
              </div>
            ))}
          </div>
          
          {/* Banner Indicators */}
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBanner(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentBanner === index 
                    ? 'bg-white w-6' 
                    : 'bg-white bg-opacity-50'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Categories Section */}
      <div className="px-4 mb-6">
        <h2 className="text-lg font-bold mb-4 text-gray-800">Shop by Category</h2>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory && setSelectedCategory(category)}
              className={`flex flex-col items-center min-w-[80px] p-3 rounded-xl transition-all ${
                selectedCategory === category
                  ? 'bg-blue-500 text-white shadow-lg transform scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="text-2xl mb-2">
                {categoryIcons[category] || '📦'}
              </span>
              <span className="text-xs font-medium text-center leading-tight">
                {category}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      <div className="p-4 products-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-800">
            {selectedCategory === 'All' ? 'Our Products' : selectedCategory}
          </h2>
          <span className="text-sm text-gray-500">
            {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
          </span>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          {paginatedProducts.map(product => {
            const isFav = favArray.some(f => f.id === product.id);
            return (
            <div key={product.id} className="bg-white rounded-xl shadow-md overflow-hidden relative">
              <div 
                className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 flex items-center justify-center relative cursor-pointer hover:scale-105 transition-transform"
                onClick={() => {
                  setSelectedProduct(product);
                  setShowProductDetails(true);
                }}
              >
                {getProductImage(product, 'large')}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (typeof toggleFavorite === 'function') {
                      toggleFavorite(product);
                    }
                  }}
                  className="absolute top-3 right-3 p-2 bg-white rounded-full shadow hover:scale-105 transition-transform"
                >
                  <Heart size={18} className={`${isFav ? 'text-pink-500' : 'text-gray-400'}`} fill={isFav ? 'currentColor' : 'none'} />
                </button>
              </div>

              <div className="p-3">
                <h3 
                  className="font-semibold text-gray-800 text-sm mb-1 cursor-pointer hover:text-blue-600 transition-colors"
                  onClick={() => {
                    setSelectedProduct(product);
                    setShowProductDetails(true);
                  }}
                >{product.name}</h3>
                <p 
                  className="text-xs text-gray-500 mb-2 cursor-pointer hover:text-gray-700 transition-colors line-clamp-2"
                  onClick={() => {
                    setSelectedProduct(product);
                    setShowProductDetails(true);
                  }}
                >{product.description}</p>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {product.sale_price && product.sale_price < product.price ? (
                      <>
                        <span className="text-sm font-bold text-red-600">₹{product.sale_price}</span>
                        <span className="text-xs text-gray-400 line-through">₹{product.price}</span>
                        <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-semibold">
                          {Math.round(((product.price - product.sale_price) / product.price) * 100)}% OFF
                        </span>
                      </>
                    ) : (
                      <span className="text-sm font-bold text-blue-600">₹{product.price}</span>
                    )}
                  </div>
                  {product.fat && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      {product.fat} fat
                    </span>
                  )}
                  {product.weight && !product.fat && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {product.weight}kg
                    </span>
                  )}
                  {product.brand && !product.fat && !product.weight && (
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                      {product.brand}
                    </span>
                  )}
                </div>

                <button
                  onClick={() => addToCart(product)}
                  className="w-full bg-blue-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors flex items-center justify-center gap-1"
                >
                  <Plus size={16} />
                  Add to Cart
                </button>

                <button
                  onClick={() => setShowSubscription(product)}
                  className="w-full bg-green-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-green-600 transition-colors mt-2"
                >
                  Subscribe & Save
                </button>
              </div>
            </div>
            );
          })}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6 mb-4">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                currentPage === 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              Previous
            </button>
            
            <div className="flex gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-10 h-10 rounded-lg font-medium transition-all ${
                    currentPage === page
                      ? 'bg-blue-500 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                currentPage === totalPages
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              Next
            </button>
          </div>
        )}

        {/* Page Info */}
        {totalPages > 1 && (
          <p className="text-center text-sm text-gray-500 mb-4">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)} of {filteredProducts.length} products
          </p>
        )}
      </div>

      {/* Floating Checkout Button */}
      {cart.length > 0 && getCartCount() > 0 && (
        <button
          onClick={openCheckout}
          className="fixed bottom-24 right-6 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-green-500/50 transition-all transform hover:scale-105 flex items-center gap-3 z-50"
        >
          <ShoppingCart size={24} />
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium">Checkout Now</span>
            <span className="text-lg font-bold">{getCartCount()} {getCartCount() === 1 ? 'item' : 'items'}</span>
          </div>
        </button>
      )}

      {/* Product Details Modal */}
      <ProductDetailsModal
        isOpen={showProductDetails}
        onClose={() => {
          setShowProductDetails(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        addToCart={addToCart}
        showToast={showToast}
        favorites={favorites}
        toggleFavorite={toggleFavorite}
        setShowSubscription={setShowSubscription}
      />
    </div>
  );
};

export default HomeScreen;